import type { SaveChoice } from "@bandstand/core/Library";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { useStores } from "../../stores/useStores";
import { Choice, ChoiceDetail, FieldLabel } from "../ui/controls";
import { Dialog } from "../ui/Dialog";
import { TextField } from "../ui/TextField";

/** One of the ways to save, with what it does. */
type SaveOption = { kind: SaveChoice["kind"]; label: string; detail: string };

/** Asks how to save the changes: overwrite, a new version, or for this setlist only. */
export const SaveDialog = observer(function SaveDialog({ onClose }: { onClose(): void }) {
    const { editor, library } = useStores();
    const from = editor.from;
    const setlist = from.kind === "setlist" ? library.setlist(from.setlistId) : undefined;
    const [kind, setKind] = useState<SaveChoice["kind"]>(setlist ? "setlistOnly" : "overwrite");
    const [name, setName] = useState("");
    const title = library.title(editor.chordSheetId!);
    const others = library.setlistsPlaying(editor.chordSheetId!).filter((playing) => playing.id !== setlist?.id);
    const alsoIn = others.length > 0 ? `, also in ${others.map((playing) => playing.name).join(", ")}` : "";

    const options: SaveOption[] = [
        { kind: "overwrite", label: "Overwrite this version", detail: `Changes "${title}" everywhere${alsoIn}.` },
        { kind: "newVersion", label: "Save as a new version", detail: `Give it its own name. "${title}" stays as it is.` },
        { kind: "setlistOnly", label: "Save for this setlist only", detail: `Only ${setlist?.name} plays from it.` },
    ];

    const choices = options.filter((choice) => choice.kind !== "setlistOnly" || setlist);

    const canSave = kind !== "newVersion" || !!name.trim();
    const placeholder = kind === "newVersion" ? "Like acoustic" : setlist?.name;

    async function save() {
        await editor.save(kind === "overwrite" ? { kind } : { kind, name: name.trim() });
        onClose();
    }

    return (
        <Dialog title="Save your changes" submit="Save" cancel="Keep editing" canSubmit={canSave} onSubmit={save} onClose={onClose}>
            {choices.map((choice) => (
                <Choice key={choice.kind}>
                    <input type="radio" name="save" checked={kind === choice.kind} onChange={() => setKind(choice.kind)} />
                    <span>
                        {choice.label}
                        <ChoiceDetail>{choice.detail}</ChoiceDetail>
                    </span>
                </Choice>
            ))}
            {kind !== "overwrite" && (
                <FieldLabel>
                    {kind === "newVersion" ? "Name of the version" : "Name (optional)"}
                    <TextField autoFocus placeholder={placeholder} value={name} onChange={setName} />
                </FieldLabel>
            )}
        </Dialog>
    );
});
